import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AdminProjectSchemas, type AdminProjectSummary } from "@workspace/contracts/projects";
import { Button } from "@workspace/ui/components/button";
import { useState } from "react";
import { orpc } from "@/api/orpc";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableActionBar } from "@/components/data-table/data-table-action-bar";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { ArchiveProject } from "@/features/project/dialogs/projects/archive";
import { DeleteProject } from "@/features/project/dialogs/projects/delete";
import { useProjectsColumns } from "@/features/project/hooks/use-project-columns";
import { useDataTable } from "@/hooks/use-data-table";
import { withDashboardPaginationDefaults } from "@/lib/pagination";
import type { DataTableRowAction } from "@/types/data-table";

export const Route = createFileRoute("/_authenticated/projects/")({
  validateSearch: (search: Record<string, unknown>) => {
    const searchWithDefaults = withDashboardPaginationDefaults(search);
    return AdminProjectSchemas.list.request.parse({
      ...searchWithDefaults,
      // nuqs owns the URL representation while TanStack Router may pass its
      // already-deserialized JSON value here. Keep route state as a string so
      // toggling Asc/Desc never leaks an array into URLSearchParams.
      sort: Array.isArray(searchWithDefaults.sort) ? JSON.stringify(searchWithDefaults.sort) : searchWithDefaults.sort,
    });
  },
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps, context: { orpc, queryClient } }) => {
    await queryClient.ensureQueryData(orpc.admin.projects.list.queryOptions({ input: deps }));
    return;
  },
});

function RouteComponent() {
  const search = Route.useSearch();

  const query = useSuspenseQuery(orpc.admin.projects.list.queryOptions({ input: search }));
  const [rowAction, setRowAction] = useState<DataTableRowAction<AdminProjectSummary> | null>(null);

  const columns = useProjectsColumns({ setRowAction });

  const { table } = useDataTable({
    columns: columns,
    data: query.data.items,
    pageCount: query.data.totalPageNumbers,
    debounceMs: 500,
    getRowId: (originalRow) => originalRow.id,
    enableSorting: true,
    initialState: { columnVisibility: { query: false, slug: false } },
    enableHiding: true,
  });

  return (
    <>
      <DataTable table={table} actionBar={<DataTableActionBar table={table} />}>
        <DataTableToolbar table={table}>
          <Button variant="outline" size="sm" asChild>
            <Link to="/projects/drafts">Drafts</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/projects/new">Create Project</Link>
          </Button>
        </DataTableToolbar>
      </DataTable>

      <DeleteProject
        open={rowAction?.variant === "delete"}
        project={rowAction?.row.original}
        onClose={() => setRowAction(null)}
      />

      <ArchiveProject
        open={rowAction?.variant === "archive"}
        project={rowAction?.row.original}
        onClose={() => setRowAction(null)}
      />
    </>
  );
}
